import type { PropsWithChildren } from 'react'
import React from 'react'
import renderHTML from 'react-html-parser'
import clsx from 'clsx'
import { useFeature } from 'toggled'

import { useSalesQuantity, useUser } from '~/hooks'
import { getQuantityRange } from '~/hooks/use-counter'
import theme from '~/truck/theme'
import type { IProduct } from '~/types/products'
import { validateIfCanJumpOnce } from '~/utils'
import { flags } from '~/utils/constants'

import { Container } from './cta-basic-content.style'

export function CTABasicContent(
  props: PropsWithChildren<{ activeVariant?: IProduct; name?: string; presentation?: string; sku?: string }>,
) {
  const { activeVariant, presentation, name, children, sku } = props

  const showSkuLabelFeature = useFeature(flags.SHOW_SKU_LABEL)

  const showProductStockFeature = useFeature(flags.SHOW_PRODUCT_STOCK)

  const handleStockFeature = useFeature(flags.HANDLE_STOCK)

  const showPriceAfterLoginFeature = useFeature(flags.SHOW_PRICE_AFTER_LOGIN)

  const user = useUser()

  const isVisitor = !user.isLoggedIn

  const hidePrice = showPriceAfterLoginFeature && isVisitor

  const canJumpAtLeastOnce = validateIfCanJumpOnce(
    activeVariant?.jumpFactor,
    activeVariant?.minimumSalesQuantity,
    activeVariant?.maximumSalesQuantity,
  )

  const { handleSalesQuantity } = useSalesQuantity()

  const { counterMinValue, counterMaxValue } = handleSalesQuantity({
    stock: activeVariant?.stock,
    minimumSalesQuantity: activeVariant?.minimumSalesQuantity,
    maximumSalesQuantity: activeVariant?.maximumSalesQuantity,
  })

  const { maxValue: maxStock } = getQuantityRange({
    minValue: counterMinValue,
    maxValue: counterMaxValue,
    jumpFactor: activeVariant?.jumpFactor,
    allowZeroToEliminate: true,
  })

  const stockToDisplay = counterMaxValue ? maxStock : activeVariant?.stock

  const shouldShowStock =
    showProductStockFeature && handleStockFeature && activeVariant?.stock > 0 && canJumpAtLeastOnce && !hidePrice

  return (
    <Container style={{ gap: theme.spacing.compact * 3 }}>
      <h1 className="text-xl font-semibold">{renderHTML(name)}</h1>
      <Container style={{ gap: theme.spacing.compact }}>
        <h3 className={clsx('text-secondary', activeVariant ? 'visible' : 'invisible')}>{presentation}</h3>
        {showSkuLabelFeature ? (
          <h3 className={clsx('text-secondary', activeVariant ? 'visible' : 'invisible')}>SKU: {sku}</h3>
        ) : null}
      </Container>
      {shouldShowStock ? (
        <p className="text-green-600">
          <span className="font-semibold">{stockToDisplay > 9999 ? '+9999' : stockToDisplay}</span> en stock
        </p>
      ) : null}
      {children}
    </Container>
  )
}
